import { filter, map } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { selectActiveChip, selectFilteredHousePlans, selectFilteredHousingUnits } from './selectors';
import LotStatus from '../enums/lotStatus';
import { TAB_QUICK_MOVE_INS } from '../constants';

/**
 * Corrects selectedTab away from its Floor Plans default when the Available chip has no floor
 * plans but does have Quick Move-Ins — otherwise a community with zero buildable plans would
 * land users on a permanently empty Floor Plans tab. Reacts only to the actions that *reset*
 * selectedTab to its default (initial lot-inventory load, chip switch, mobile-modal reopen), not
 * to tabSelected — a user who manually clicks into an empty Floor Plans tab must not be bounced
 * back out.
 *
 * Only the Available chip has tabs (see services/listViewContentService.js), so this checks
 * selectActiveChip explicitly rather than relying on selectedTab's null-vs-TAB_FLOOR_PLANS shape
 * for other chips to imply the same thing.
 *
 * fetchLotInventorySuccess covers the true initial load: before it fires, housePlans/
 * housingUnits are still empty because the fetch hasn't landed yet, not because there's genuinely
 * nothing to show (same reasoning as selectors.js#selectIsLotInventorySettled), so this must not
 * run any earlier. If both Floor Plans and Quick Move-Ins are empty, selectedTab is left alone —
 * Floor Plans stays the default and renders its existing empty state.
 *
 * @param {object} params
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @returns {import('redux-observable').Epic}
 */
function createAvailableTabFallbackEpic({ dataActions }) {
  return (action$, state$) => action$.pipe(
    ofType(
      dataActions.fetchLotInventorySuccess.type,
      dataActions.chipSelected.type,
      dataActions.viewReset.type,
    ),
    map(() => {
      const state = state$.value;
      const isAvailableChip = selectActiveChip(state) === LotStatus.Available.toString();
      const hasNoFloorPlans = selectFilteredHousePlans(state).length === 0;
      const hasQuickMoveIns = selectFilteredHousingUnits(state).length > 0;

      if (isAvailableChip && hasNoFloorPlans && hasQuickMoveIns) {
        return dataActions.tabSelected(TAB_QUICK_MOVE_INS);
      }
      return null;
    }),
    filter(Boolean),
  );
}

export default createAvailableTabFallbackEpic;
